import axios from 'axios';

// Use environment variable for API URL
const API_BASE = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('crm_chat_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    // Only logout on actual authentication errors, not temporary connection issues
    if (err.response?.status === 401 && err.response?.data?.error !== 'Authentication failed') {
      localStorage.removeItem('crm_chat_token');
      window.location.href = '/';
    }
    return Promise.reject(err.response?.data || { error: 'Network error' });
  }
);

// ✅ FIX: convert relative /uploads/path → full backend URL (port 5000, not 3000)
export function getFileUrl(filePath) {
  if (!filePath) return '';
  if (filePath.startsWith('http')) return filePath;
  return `${API_BASE}${filePath}`;
}

// Build a download URL that preserves the original filename via Content-Disposition.
// filePath: stored path e.g. "/uploads/1748234556-abc1234.pdf"
// originalName: the name the user uploaded, e.g. "MyDocument.pdf"
export function getFileDownloadUrl(filePath, originalName) {
  if (!filePath) return '';
  const storedFilename = filePath.split('/').pop();
  const nameParam = encodeURIComponent(originalName || storedFilename);
  return `${API_BASE}/api/download/${encodeURIComponent(storedFilename)}?name=${nameParam}`;
}

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  getUsers: () => api.get('/auth/users'),
};

export const groupsAPI = {
  getAll: () => api.get('/groups'),
  getById: (id) => api.get(`/groups/${id}`),
  createFromCampaign: (data) => api.post('/groups/from-campaign', data),
  createFromCampaignData: (data) => api.post('/groups/from-campaign-data', data),
  createCustom: (data) => api.post('/groups/custom', data),
  expandHierarchy: (selectedUsers) => api.post('/groups/expand-hierarchy', { user_ids: selectedUsers }),
  getUsersByRoles: () => api.get('/groups/users-by-roles'),
  addMember: (groupId, userId) => api.post(`/groups/${groupId}/members`, { user_id: userId }),
  removeMember: (groupId, userId) => api.delete(`/groups/${groupId}/members/${userId}`),
  getSummary: (groupId) => api.get(`/groups/${groupId}/summary`),
};

export const messagesAPI = {
  getMessages: (groupId, page = 1) => api.get(`/messages/${groupId}?page=${page}`),
  sendMessage: (groupId, data) => api.post(`/messages/${groupId}`, data),
  uploadFile: (groupId, formData) => api.post(`/messages/${groupId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteMessage: (groupId, messageId) => api.delete(`/messages/${groupId}/${messageId}`),
  editMessage: (groupId, messageId, content) => api.patch(`/messages/${groupId}/${messageId}`, { content }),
  addReaction: (groupId, messageId, emoji) => api.post(`/messages/${groupId}/${messageId}/reaction`, { emoji }),
  removeReaction: (groupId, messageId, emoji) => api.delete(`/messages/${groupId}/${messageId}/reaction`, { data: { emoji } }),
  getPinnedMessages: (groupId) => api.get(`/messages/${groupId}/pinned`),
  pinMessage: (groupId, messageId) => api.post(`/messages/${groupId}/${messageId}/pin`),
  unpinMessage: (groupId, messageId) => api.delete(`/messages/${groupId}/${messageId}/pin`),
  getUnreadCounts: () => api.get('/messages/unread-counts'),
  markGroupFullyRead: (groupId) => api.post(`/messages/${groupId}/mark-all-read`),
  // 🆕 New endpoints for user assignment
  getRecipients: (groupId) => api.get(`/messages/${groupId}/recipients`),
  getAssignmentInfo: (groupId, recipientId) => api.get(`/messages/${groupId}/assignment/${recipientId}`),
  searchMessages: (groupId, q, before) => api.get(`/messages/${groupId}/search`, { params: { q, before } }),
};

export const tasksAPI = {
  getByGroup: (groupId) => api.get(`/tasks/group/${groupId}`),
  getById: (taskId) => api.get(`/tasks/${taskId}`),
  create: (data) => {
    if (data instanceof FormData) {
      return api.post('/tasks', data, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/tasks', data);
  },
  updateStatus: (taskId, status, comment, feedback) => api.patch(`/tasks/${taskId}/status`, { status, comment, feedback }),
  getPendingCounts: () => api.get('/tasks/pending-counts'),
  getResponses: (taskId) => api.get(`/tasks/${taskId}/responses`),
  createFollowup: (data) => api.post('/tasks/followup', data),
  getFollowups: (groupId) => api.get(`/tasks/followups/group/${groupId}`),
};

export const campaignsAPI = {
  getAll: () => api.get('/campaigns'),
  getById: (id) => api.get(`/campaigns/${id}`),
  getCampaignData: () => api.get('/campaigns/campaign-data'),
  getPidStatus: (groupId) => api.get(`/campaigns/pid-status/group/${groupId}`),
  getSharedPids: (groupId) => api.get(`/campaigns/shared-pids/group/${groupId}`),
  debugFindPidMessages: () => api.get('/campaigns/debug/find-pid-messages'),
  debugFindPidTasks: () => api.get('/campaigns/debug/find-pid-tasks'),
  updatePidStatus: (data) => api.post('/campaigns/pid-status', data),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
};

export default api;
